import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { Layout, Row, Col, Card, Spin, Alert, Divider, Typography, Space } from 'antd';
import { fetchTickerData } from "../../../Api/tickersApi";
import {fetchDetailedPortfolioObjects} from "../../../Api/portfolioApi";
import usePortfolioTimeseries from "../../PortfolioView/PortfolioChart/usePortfolioTimeseries";
import MultiStockChart from "../../../SuperComponents/StockCharts/MultiStockChart";
import BenchmarkMetrics from "./BenchmarkMetrics";
import {portfolioTypeString} from "../../../utils/strings";
import {DoubleLine} from "../../../Components/Labels";
import {Brand} from "../../../Components/Brand";
import {useEnsureTickersLoaded} from "../../../hooks/useEnsureTickersLoaded";
import TickerChip from "../../../Components/TickerChip";
import {getTickersBySymbols} from "../../../Store/slices/globalResources.slice";
import {TrackChangesRounded} from "@mui/icons-material";
import usePdfExport from "../../../hooks/usePdfExport";
import Toolbar from "./Toolbar";


const TitleBlock = ({text, bg='var(--primary)'})=>(
    <div style={{width:'100%', padding: '15px 10px', background: bg}}>
        <Typography.Text style={{color: '#fff'}}>{text}</Typography.Text>
    </div>
)

const FactSheet = () => {
    const { portfolioId } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [editMode, setEditMode] = useState(true);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [errorDetails, setErrorDetails] = useState(null);
    const portfolio = useSelector(state => state.portfolios.items.find(p => p.id === portfolioId));
    const portfolioStats = useSelector(state => state.tickers.tickers[portfolioId]);
    const symbols = useMemo(() => [portfolioId, 'spy'], [portfolioId]);  // 'SPY' as default benchmark
    const startDate = useMemo(() => '2020-01-01', []);  // Static start date
    const tickerPool = Object.keys(portfolio?.symbols ?? {});
    const { tickers } = useEnsureTickersLoaded(tickerPool || ['spy']);
    const tickerDetails = useSelector(state => getTickersBySymbols(state, tickerPool));
    const { timeSeriesData, loading, error } = usePortfolioTimeseries(symbols, startDate);
    const { exportToPDF, componentRef, isExporting } = usePdfExport({
        filename: 'benchmark_metrics.pdf',
        onBeforeExport: () => setEditMode(false),
        onAfterExport: () => setEditMode(true),
      });

    const handleExport = () => {
        exportToPDF();
    }


    // Effect to fetch portfolio stats on mount or when portfolioId changes
    useEffect(() => {
        if (portfolioId) {
            setLoadingDetails(true);
            dispatch(fetchDetailedPortfolioObjects([portfolioId]));
            dispatch(fetchTickerData({
                symbols: [portfolioId],
                fields: ['stats.benchmarks.spy'],
                collection: 'stats'
            }))
            .then(response => {
                setLoadingDetails(false);
                setErrorDetails(null);
                console.log(response);
            })
            .catch(err => {
                setLoadingDetails(false);
                setErrorDetails('Failed to fetch portfolio data');
                console.error(err);
            });
        }
    }, [dispatch, portfolioId]);

    // Redirect or show error if portfolio is not found

    // Combine loading states and error messages
    const isLoading = loadingDetails || loading;
    const errorMessage = errorDetails || error;

    // Render loading or error state
    if (isLoading) return <Spin size="large" />;
    if (errorMessage) return <Alert message={errorMessage} type="error" showIcon />;

    console.log(tickerDetails)

    // Main render
    return (
        <Layout style={{background: 'var(--offwhite)'}}>
            <Layout.Content style={{maxWidth: '1200px', margin: 'auto', background: "#FFF", padding: '0 30px'}}>
                <div style={{padding: '20px 0'}}>
                <Toolbar onExportPDF={handleExport} editMode={editMode} toggleEditMode={()=>setEditMode(!editMode)} />
                </div>
                <div ref={componentRef} style={{width: '8.5in', margin: 0, background: "#FFF"}}>
                    <Row style={{background: "var(--color-primary)"}}
                         align={'middle'}
                         justify={'space-between'}
                         className={'px-3'}
                    >
                        <Space>
                            <Brand maxHeight={70}/>
                            <DoubleLine fontColor={'#fff'} title={portfolio?.name} text={portfolioTypeString(portfolio?.type)} />
                        </Space>
                        <Space>
                            <Typography.Title level={3} style={{color: "#aaccff"}}>
                                <TrackChangesRounded color={'primary'} fontSize={'large'} sx={{marginRight: 2}}/>
                                {portfolio?.targets?.equity} / {portfolio?.targets?.fixed}
                            </Typography.Title>
                        </Space>
                    </Row>
                    {portfolio?.attr?.summary && portfolio?.attr?.summary?.length > 0 && (
                        <Row justify={'center'}>
                            <Col flex={'0 0 8in'}>
                                <Typography.Paragraph style={{fontFamily: "Poppins, sans-serif", paddingTop: 20, fontWeight: '500'}}>
                                    {portfolio?.attr?.summary ?? ''}
                                </Typography.Paragraph>
                            </Col>
                        </Row>
                    )}
                    <div style={{padding: '10px 0'}}>
                        <MultiStockChart symbols={[portfolioId, 'spy']}
                                         labelMapping={{[portfolioId]: portfolio?.name, spy: "S&P 500"}}
                                         stockChartProps={{
                                             showRangeSelector: false
                                         }}
                                         height={300}
                                         heightOffset={0}
                                         xPadding={0}
                                         yPadding={0}

                        />
                        <Typography.Paragraph style={{fontSize: '10px', paddingLeft: '50px'}}>
                            Illustrative Performance of the Portfolio vs Benchmark.
                        </Typography.Paragraph>
                    </div>
                    {portfolioStats && (
                        <div style={{padding: '10px 0'}}>
                            <TitleBlock text={'Portfolio vs Benchmark Metrics'} bg={'#38B6FF'}/>
                            <div style={{padding: '20px 20px 0 20px'}}>
                                <BenchmarkMetrics benchmarkMetrics={portfolioStats?.stats?.benchmarks?.spy} editMode={editMode}/>
                            </div>
                        </div>
                    )}
                    <div style={{padding: '10px 0'}}>
                        <TitleBlock text={'Portfolio Options (Pool of Tickers)'} bg={'#6D869B'}/>
                        <Row wrap={true} style={{paddingTop: '10px', paddingLeft: '10px'}}>
                            {Array.isArray(tickerPool) && tickerPool.length>0 && tickerPool.map(symbol => (
                                <Col key={symbol} flex={'0 0 3.5in'}>
                                    <TickerChip ticker={tickerDetails.find(x=>x.symbol===symbol)} />
                                </Col>
                            ))}
                        </Row>
                    </div>
                </div>
            </Layout.Content>

        </Layout>
    );
}

export default FactSheet;
